/**
 * Generated by @openapi-codegen
 *
 * @version 2.2.0
 */
import * as reactQuery from '@tanstack/react-query'
import {
  BackofficeApiContext,
  useBackofficeApiContext,
} from './backofficeApiContext'
import type * as Fetcher from './backofficeApiFetcher'
import { backofficeApiFetch } from './backofficeApiFetcher'
import type * as Schemas from './backofficeApiSchemas'

export type GetVersionError = Fetcher.ErrorWrapper<undefined>

export type GetVersionResponse = {
  buildId: string
  version: string
}

export type GetVersionVariables = BackofficeApiContext['fetcherOptions']

export const fetchGetVersion = (
  variables: GetVersionVariables,
  signal?: AbortSignal,
) =>
  backofficeApiFetch<
    GetVersionResponse,
    GetVersionError,
    undefined,
    {},
    {},
    {}
  >({ url: '/version', method: 'get', ...variables, signal })

export const useGetVersion = <TData = GetVersionResponse>(
  variables: GetVersionVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetVersionResponse, GetVersionError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useBackofficeApiContext(options)
  return reactQuery.useQuery<GetVersionResponse, GetVersionError, TData>({
    queryKey: queryKeyFn({
      path: '/version',
      operationId: 'getVersion',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetVersion({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type GetMeError = Fetcher.ErrorWrapper<undefined>

export type GetMeVariables = BackofficeApiContext['fetcherOptions']

export const fetchGetMe = (variables: GetMeVariables, signal?: AbortSignal) =>
  backofficeApiFetch<Schemas.StaffIdentity, GetMeError, undefined, {}, {}, {}>({
    url: '/api/v1/me',
    method: 'get',
    ...variables,
    signal,
  })

export const useGetMe = <TData = Schemas.StaffIdentity>(
  variables: GetMeVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.StaffIdentity, GetMeError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useBackofficeApiContext(options)
  return reactQuery.useQuery<Schemas.StaffIdentity, GetMeError, TData>({
    queryKey: queryKeyFn({
      path: '/api/v1/me',
      operationId: 'getMe',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchGetMe({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type ListAuditEntriesQueryParams = {
  /**
   * Only entries at or after this time, in epoch milliseconds.
   *
   * @minimum 0
   * @maximum 999999999999999
   */
  from?: number
  /**
   * Only entries at or before this time, in epoch milliseconds (its whole millisecond).
   *
   * @minimum 0
   * @maximum 999999999999999
   */
  to?: number
  /**
   * Only entries for this cluster id.
   *
   * @minLength 1
   */
  hiveId?: string
  /**
   * Only entries for this organization id.
   *
   * @minLength 1
   */
  orgId?: string
  /**
   * Only entries for this license id.
   *
   * @minLength 1
   */
  licenseId?: string
  /**
   * Only entries with this action, e.g. `license.reissue`.
   *
   * @minLength 1
   */
  action?: string
  /**
   * Only entries whose actor has this email.
   *
   * @minLength 1
   */
  user?: string
  /**
   * Only entries whose actor has this id (the staff member's login).
   *
   * @minLength 1
   */
  userId?: string
  /**
   * Only entries with this outcome.
   */
  status?: 'success' | 'error' | 'denied'
  /**
   * The most entries the page holds. Default 100.
   *
   * @minimum 1
   * @maximum 100
   */
  limit?: number
  /**
   * The previous page's `nextCursor`, to read the page after it. Opaque; valid with any filters.
   */
  cursor?: string
}

export type ListAuditEntriesError = Fetcher.ErrorWrapper<undefined>

export type ListAuditEntriesVariables = {
  queryParams?: ListAuditEntriesQueryParams
} & BackofficeApiContext['fetcherOptions']

/**
 * backoffice-api's own audit log. Filters are exact, case-sensitive and combined with AND. Pages with a cursor; no total count.
 */
export const fetchListAuditEntries = (
  variables: ListAuditEntriesVariables,
  signal?: AbortSignal,
) =>
  backofficeApiFetch<
    Schemas.AuditLogPage,
    ListAuditEntriesError,
    undefined,
    {},
    ListAuditEntriesQueryParams,
    {}
  >({ url: '/api/v1/audit', method: 'get', ...variables, signal })

/**
 * backoffice-api's own audit log. Filters are exact, case-sensitive and combined with AND. Pages with a cursor; no total count.
 */
export const useListAuditEntries = <TData = Schemas.AuditLogPage>(
  variables: ListAuditEntriesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.AuditLogPage,
      ListAuditEntriesError,
      TData
    >,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } =
    useBackofficeApiContext(options)
  return reactQuery.useQuery<
    Schemas.AuditLogPage,
    ListAuditEntriesError,
    TData
  >({
    queryKey: queryKeyFn({
      path: '/api/v1/audit',
      operationId: 'listAuditEntries',
      variables,
    }),
    queryFn: ({ signal }) =>
      fetchListAuditEntries({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type QueryOperation =
  | {
      path: '/version'
      operationId: 'getVersion'
      variables: GetVersionVariables
    }
  | {
      path: '/api/v1/me'
      operationId: 'getMe'
      variables: GetMeVariables
    }
  | {
      path: '/api/v1/audit'
      operationId: 'listAuditEntries'
      variables: ListAuditEntriesVariables
    }
